/**
 * @file     ContactFields.tsx
 * @purpose  First name, last name, email and mobile phone, all required. The form renders FormConsentLine under its submit button.
 */
'use client';

import Box from '@mui/material/Box';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import { useTranslation } from 'react-i18next';
import { formatPhoneInput } from '@/utils/formatPhone';
import { contactLabels, type ContactErrors, type ContactField, type ContactValues } from '@/services/leads/contactFields';

type Props = {
  values: ContactValues;
  errors?: ContactErrors;
  onChange: (field: ContactField, value: string) => void;
  /** Hide the email field when an earlier step already took it. */
  hideEmail?: boolean;
  /** One line under the phone label, only where the phone needs a reason. */
  phoneReason?: string;
  size?: TextFieldProps['size'];
  disabled?: boolean;
  idPrefix?: string;
};

export function ContactFields({
  values,
  errors = {},
  onChange,
  hideEmail = false,
  phoneReason,
  size = 'medium',
  disabled = false,
  idPrefix = 'contact',
}: Props) {
  const { t } = useTranslation();
  const labels = contactLabels(t);
  const field = (name: ContactField, extra: Partial<TextFieldProps>) => (
    <TextField
      id={`${idPrefix}-${name}`}
      name={name}
      label={labels[name]}
      value={values[name]}
      onChange={(e) => onChange(name, name === 'phone' ? formatPhoneInput(e.target.value) : e.target.value)}
      error={Boolean(errors[name])}
      helperText={errors[name] || (name === 'phone' ? phoneReason : undefined)}
      required
      fullWidth
      size={size}
      disabled={disabled}
      {...extra}
    />
  );

  return (
    <Box sx={{ display: 'grid', gap: 4 }}>
      <Box sx={{ display: 'grid', gap: 4, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
        {field('firstName', { autoComplete: 'given-name' })}
        {field('lastName', { autoComplete: 'family-name' })}
      </Box>
      {hideEmail ? null : field('email', { type: 'email', autoComplete: 'email', inputProps: { inputMode: 'email' } })}
      {field('phone', {
        type: 'tel',
        autoComplete: 'tel',
        placeholder: t('forms.contact.phonePlaceholder', { defaultValue: '(604) 555-0123' }),
        inputProps: { inputMode: 'tel' },
      })}
    </Box>
  );
}
